export const FAQS = [
  {
    slug: "shingle-vs-metal-roof-ontario",
    question: "Shingle or metal roof: which is better for an Ontario home?",
    category: "Materials",
    description:
      "A practical comparison of asphalt shingles and metal roofing for Ontario weather, budget, lifespan, and resale expectations.",
    heroTitle: "Shingle or Metal Roof?",
    intro:
      "For most Ontario homeowners, the right roof is not about chasing the longest brochure lifespan. It is about matching the roof system to the house, the budget, the expected ownership timeline, and the installation details that matter in freeze-thaw conditions.",
    sections: [
      {
        heading: "Asphalt shingles make sense when budget and simplicity matter most",
        paragraphs: [
          "Architectural shingles remain the most common residential roofing material in Ontario because they are widely available, cost-effective, and appropriate for many detached homes, semis, and townhomes.",
          "A well-installed shingle system is easier to phase into an insurance claim, easier to match on surrounding homes, and usually less expensive to repair if isolated wind or impact damage occurs later.",
        ],
      },
      {
        heading: "Metal roofing can make sense when longevity and snow shedding are priorities",
        paragraphs: [
          "Metal systems can offer a longer service life, strong wind performance, and cleaner snow-shedding behavior on the right house. They also change the sound profile, detailing complexity, and upfront project cost.",
          "The quality difference between a properly detailed metal roof and an average one is substantial. Fastener layout, trim profiles, ventilation, transitions, and flashing details matter more than the marketing language.",
        ],
      },
      {
        heading: "The Ontario-specific decision points",
        paragraphs: [
          "In Ontario, heavy snow loads, freeze-thaw cycling, ice dam risk, and ventilation performance matter. Those issues affect both systems, but poor attic ventilation and weak flashing details tend to shorten roof life regardless of material.",
          "If the house has complex valleys, dormers, chimneys, skylights, and wall transitions, the labor quality becomes more important than the material category itself.",
        ],
      },
    ],
    bullets: [
      "Choose shingles when lower upfront cost and easier future repairs are the main priorities.",
      "Choose metal when a longer ownership horizon and higher upfront investment make sense.",
      "Do not compare products without also comparing underlayment, flashing, ventilation, and installer quality.",
      "For most homes, bad detailing will erase the theoretical advantage of a premium material.",
    ],
    takeaway:
      "For many Ontario homes, architectural shingles are the practical default. Metal becomes attractive when the budget supports it, the house suits it, and the installation team can execute the details properly.",
  },
  {
    slug: "cost-of-new-roof-ontario",
    question: "How much does it cost to get a new roof in Ontario?",
    category: "Pricing",
    description:
      "What drives roof replacement pricing in Ontario, from roof size and slope to decking repairs, ventilation, and warranty scope.",
    heroTitle: "How Much Does a New Roof Cost in Ontario?",
    intro:
      "There is no honest single-number answer for roof replacement pricing in Ontario. Real cost depends on roof size, pitch, access, tear-off conditions, ventilation upgrades, flashing complexity, disposal, and whether hidden substrate repairs show up once the old roof is removed.",
    sections: [
      {
        heading: "The main cost drivers",
        paragraphs: [
          "Square footage is only the starting point. A simple walkable roof with clean access is a different project from a steep multi-level roof with dormers, valleys, skylights, and tight driveway access.",
          "Material tier matters, but labor complexity often matters more. Ventilation improvements, step flashing replacement, chimney work, plywood replacement, and cleanup standards can materially change the total.",
        ],
      },
      {
        heading: "What homeowners should expect in a quote",
        paragraphs: [
          "A useful quote should identify the roofing system being installed, the tear-off scope, underlayment type, flashing treatment, ventilation plan, cleanup method, and the assumptions built into the price.",
          "If a proposal looks unusually low, it often means some parts of the scope are being minimized, excluded, or left vague until the job is underway.",
        ],
      },
      {
        heading: "Why price ranges online are often misleading",
        paragraphs: [
          "Generic online calculators flatten too many variables. Two houses with the same footprint can have meaningfully different replacement costs because one has multiple transitions, difficult access, and substrate repairs while the other does not.",
          "That is why inspection-driven quoting is more useful than broad online averages.",
        ],
      },
    ],
    bullets: [
      "Roof size and slope affect labor time and material quantity.",
      "Valleys, skylights, chimneys, and wall transitions increase detailing complexity.",
      "Ventilation upgrades and plywood replacement can materially change price.",
      "Cleanup, disposal, permit requirements, and warranty scope should be spelled out.",
    ],
    takeaway:
      "The right question is not only 'what does a roof cost?' but 'what scope is included, what assumptions are built in, and what conditions could change the final number?'",
  },
  {
    slug: "repair-or-replace-roof",
    question: "Should I repair my roof or replace it?",
    category: "Decision Making",
    description:
      "How to think through localized repairs versus full replacement based on roof age, failure pattern, and remaining service life.",
    heroTitle: "Repair or Replace?",
    intro:
      "The correct answer depends on whether the issue is isolated or systemic. A localized defect on an otherwise serviceable roof is a repair problem. Repeated leaks, broad granule loss, brittle shingles, failed flashing at multiple transitions, or chronic attic moisture issues point toward replacement planning.",
    sections: [
      {
        heading: "When repair usually makes sense",
        paragraphs: [
          "Repairs are often appropriate when a leak can be traced to a specific flashing defect, missing shingles after wind, isolated storm damage, or a single problematic transition.",
          "If the roof still has meaningful remaining life and the rest of the system is holding, targeted work can be the responsible option.",
        ],
      },
      {
        heading: "When replacement becomes the better decision",
        paragraphs: [
          "Replacement becomes more defensible when multiple slopes are failing, repairs are stacking up year after year, or the roof is near the end of its service life and hidden issues are likely to keep surfacing.",
          "At that point, repeated repair spending often delays a decision without reducing long-term cost.",
        ],
      },
      {
        heading: "What a proper assessment should answer",
        paragraphs: [
          "A useful assessment should identify whether the issue is isolated or widespread, whether the attic and ventilation conditions are contributing, and whether the roof can realistically deliver more service life after repair.",
          "Without that context, both 'just patch it' and 'replace everything' can be poor advice.",
        ],
      },
    ],
    bullets: [
      "Repair isolated defects on roofs with real remaining service life.",
      "Replace when failures are recurring, widespread, or age-related.",
      "Check attic ventilation and moisture conditions before deciding.",
      "Base the decision on roof condition, not pressure or guesswork.",
    ],
    takeaway:
      "The decision should come from condition, failure pattern, and remaining service life, not from the size of the sales pitch.",
  },
  {
    slug: "how-long-does-roof-last-ontario",
    question: "How long does a roof last in Ontario?",
    category: "Lifespan",
    description:
      "A realistic look at roofing lifespan in Ontario and why ventilation, installation quality, and weather exposure matter as much as the product label.",
    heroTitle: "How Long Does a Roof Last in Ontario?",
    intro:
      "Roof lifespan in Ontario is not determined by the packaging alone. Manufacturer branding matters less than the combined effect of installation quality, attic ventilation, weather exposure, slope orientation, and maintenance history.",
    sections: [
      {
        heading: "Material lifespan is only one part of the picture",
        paragraphs: [
          "Architectural shingles can deliver a solid service life when the system is detailed properly. Poor ventilation, weak flashing work, and skipped accessories can shorten that substantially.",
          "Metal systems may last longer in theory, but only if panel layout, fasteners, trims, and transitions are handled correctly.",
        ],
      },
      {
        heading: "Ontario climate changes the equation",
        paragraphs: [
          "Freeze-thaw cycling, snow accumulation, wind-driven rain, and ice buildup around eaves all accelerate wear when the roof design or ventilation strategy is weak.",
          "South-facing slopes often age differently from shaded ones, and tree cover can increase moisture retention and debris accumulation.",
        ],
      },
      {
        heading: "Signs the roof is aging faster than expected",
        paragraphs: [
          "Granule loss, curling, brittleness, repeated leak repairs, staining in the attic, uneven shingle courses, and flashing fatigue are all signs the system may be moving out of its repair-friendly window.",
          "The earlier those conditions are documented, the better the planning decisions tend to be.",
        ],
      },
    ],
    bullets: [
      "Installation quality and ventilation have major influence on service life.",
      "Ontario weather can shorten lifespan when detailing is weak.",
      "Different slopes on the same house can age at different rates.",
      "Recurring leak history is often a stronger warning sign than age alone.",
    ],
    takeaway:
      "A roof lasts as long as the whole system performs, not as long as the packaging implies.",
  },
  {
    slug: "roof-inspection-before-buying-house",
    question: "Should I get a roof inspection before buying a house?",
    category: "Inspections",
    description:
      "Why a roof inspection before purchase can change negotiation leverage, budgeting, and risk planning for Ontario buyers.",
    heroTitle: "Roof Inspection Before Buying a House",
    intro:
      "A general home inspection is useful, but it does not always provide enough roofing detail to support a large repair or replacement decision. If the roof condition could affect negotiation, insurance, or near-term capital planning, a focused roof inspection can be worthwhile.",
    sections: [
      {
        heading: "Why pre-purchase roof clarity matters",
        paragraphs: [
          "Roofing issues are expensive, disruptive, and easy to underestimate from the ground. A house can look acceptable during a showing while still having advanced wear, poor flashing details, or ventilation problems hidden in the attic.",
          "Understanding roof condition before closing helps avoid immediate post-purchase surprises.",
        ],
      },
      {
        heading: "What the inspection should answer",
        paragraphs: [
          "A focused inspection should identify overall roof age signals, visible failure points, probable remaining life, active or recent leak evidence, and whether repair or replacement planning should be expected soon.",
          "It should also flag whether more invasive investigation or attic review is warranted.",
        ],
      },
      {
        heading: "How buyers use the findings",
        paragraphs: [
          "Buyers typically use roof findings for negotiation, reserve budgeting, timing decisions, and to understand whether a new purchase will immediately need major capital work.",
          "That is more useful than a vague statement that the roof is 'older but serviceable.'",
        ],
      },
    ],
    bullets: [
      "A specialized roof inspection can expose issues a general inspection may only note briefly.",
      "Findings can support negotiation and budgeting decisions before closing.",
      "Remaining service life matters as much as whether the roof leaks today.",
      "Attic moisture and ventilation conditions should be part of the evaluation.",
    ],
    takeaway:
      "If the purchase decision depends on roof risk, a focused inspection is usually cheaper than being wrong afterward.",
  },
  {
    slug: "insurance-cover-roof-damage-ontario",
    question: "Will insurance cover roof damage in Ontario?",
    category: "Insurance",
    description:
      "What homeowners should understand about storm-related roof claims, documentation, exclusions, and realistic expectations in Ontario.",
    heroTitle: "Will Insurance Cover Roof Damage?",
    intro:
      "Insurance may cover sudden, accidental roof damage, but not every roofing problem becomes an insurance claim. Coverage often depends on the cause of loss, the condition of the roof before the event, and the policy wording.",
    sections: [
      {
        heading: "Claims are stronger when the damage is event-driven",
        paragraphs: [
          "Wind damage, hail, or a specific storm event is generally easier to document than long-term wear, deferred maintenance, or age-related deterioration.",
          "The stronger the documentation connecting the observed damage to a recent event, the more straightforward the discussion tends to be.",
        ],
      },
      {
        heading: "What insurers often push back on",
        paragraphs: [
          "Older roofs, poor prior workmanship, ongoing leaks, and signs of neglected maintenance can complicate or reduce coverage. Insurance is not a maintenance plan.",
          "That is why inspection photos, timestamps, and clear condition notes matter when damage is recent.",
        ],
      },
      {
        heading: "How homeowners should approach it",
        paragraphs: [
          "The first step is to document the condition honestly and determine whether the issue is likely storm-related, isolated, or long-term. From there, the homeowner can decide whether opening a claim is appropriate.",
          "Claims should be handled with clear expectations, not assumptions that every damaged roof will be replaced in full.",
        ],
      },
    ],
    bullets: [
      "Sudden storm-related damage is more likely to be covered than normal wear.",
      "Pre-existing deterioration can reduce or complicate a claim.",
      "Good photo documentation strengthens the claim conversation.",
      "A condition assessment should happen before broad promises are made.",
    ],
    takeaway:
      "Insurance may help with sudden covered damage, but documentation and realistic scope assessment are what keep the process grounded.",
  },
  {
    slug: "best-time-of-year-to-replace-roof-ontario",
    question: "What is the best time of year to replace a roof in Ontario?",
    category: "Planning",
    description:
      "How season, temperature, scheduling pressure, and weather risk affect roof replacement timing in Ontario.",
    heroTitle: "Best Time of Year to Replace a Roof",
    intro:
      "Ontario roof replacements can be completed across much of the year, but timing still affects scheduling, weather exposure, and project risk. The best season is usually the one that balances stable weather, installer availability, and the urgency of the roof condition.",
    sections: [
      {
        heading: "Spring and fall are often the easiest planning windows",
        paragraphs: [
          "Moderate temperatures and more predictable working conditions make spring and fall attractive for many residential roofing projects.",
          "Those seasons also tend to fill up quickly, so waiting for the perfect calendar window can mean losing access to the better crews.",
        ],
      },
      {
        heading: "Summer is common but not automatically better",
        paragraphs: [
          "Summer offers long daylight hours and strong production capacity, but it also comes with heat, storm interruptions, and heavier contractor demand.",
          "A summer project can still be the right choice if the scope is defined clearly and the schedule is managed properly.",
        ],
      },
      {
        heading: "Condition matters more than the calendar when the roof is failing",
        paragraphs: [
          "If leaks are active, decking is deteriorating, or shingles are lifting regularly, waiting for an ideal season can create more damage and more expense.",
          "In that situation, urgency usually matters more than seasonality.",
        ],
      },
    ],
    bullets: [
      "Spring and fall are popular because temperatures are moderate and schedules are manageable.",
      "Summer can work well, but demand and weather interruptions often increase.",
      "If the roof is actively failing, condition matters more than timing preferences.",
      "Book earlier than you think if you want flexibility on crew availability.",
    ],
    takeaway:
      "The best time to replace a roof in Ontario is when weather, schedule, and roof condition align, not when the calendar feels ideal in theory.",
  },
  {
    slug: "signs-you-need-new-roof",
    question: "What are the signs you need a new roof?",
    category: "Warning Signs",
    description:
      "The practical signs that a roof may be moving from repair territory into replacement planning.",
    heroTitle: "Signs You May Need a New Roof",
    intro:
      "A roof rarely fails all at once. More often, replacement becomes appropriate after a pattern of visible wear, recurring leaks, and system fatigue starts to show up across multiple parts of the roof.",
    sections: [
      {
        heading: "Visible wear is only part of the story",
        paragraphs: [
          "Curling shingles, missing tabs, exposed fasteners, sagging areas, and heavy granule loss are obvious warning signs, but they are not the only ones that matter.",
          "A roof can also be nearing replacement because the flashing system is failing, the attic is showing moisture issues, or the repair history is getting longer every year.",
        ],
      },
      {
        heading: "Recurring leaks are usually more important than one isolated issue",
        paragraphs: [
          "A single repairable leak is not proof that the whole roof should be replaced. Repeated problems in different locations are much more significant.",
          "When different parts of the system begin to fail at the same time, replacement planning becomes more reasonable.",
        ],
      },
      {
        heading: "Context matters",
        paragraphs: [
          "Age, attic ventilation, roof complexity, surrounding tree cover, and previous workmanship quality all influence whether the roof still has useful life left.",
          "That is why visible signs should be interpreted as part of a condition assessment, not in isolation.",
        ],
      },
    ],
    bullets: [
      "Look for repeated leak history, not just one visible defect.",
      "Granule loss, curling, brittleness, and flashing failure are key warning signs.",
      "Attic staining and moisture can indicate roof system problems even when the exterior looks passable.",
      "Replacement decisions should be based on pattern and condition, not age alone.",
    ],
    takeaway:
      "The strongest signal that you need a new roof is not one symptom, but a pattern showing the system is losing reliability.",
  },
  {
    slug: "how-long-does-roof-replacement-take",
    question: "How long does a roof replacement take?",
    category: "Planning",
    description:
      "What affects roofing project duration, from roof size and weather to complexity, access, and hidden repairs.",
    heroTitle: "How Long Does Roof Replacement Take?",
    intro:
      "Many residential roof replacements can be completed quickly once work begins, but timelines still vary based on complexity, weather, access, and whether hidden substrate repairs appear during tear-off.",
    sections: [
      {
        heading: "Simple roofs move faster than complex ones",
        paragraphs: [
          "A straightforward roof with good access, limited penetrations, and standard slopes can move much faster than a steep multi-level roof with dormers, skylights, valleys, and wall transitions.",
          "Project duration is usually driven by detail complexity more than by the basic footprint alone.",
        ],
      },
      {
        heading: "Weather and hidden conditions affect schedule certainty",
        paragraphs: [
          "Rain, high winds, freezing conditions, and unexpected deck repairs can all change the timeline once the project starts.",
          "That is normal. A good schedule should leave room for real site conditions instead of pretending every roof is predictable.",
        ],
      },
      {
        heading: "The better question is often 'how long will my property be exposed?'",
        paragraphs: [
          "Homeowners usually care less about total scheduling theory and more about whether the crew is organized, whether the roof is protected properly overnight if needed, and whether cleanup stays controlled.",
          "Process quality matters as much as raw speed.",
        ],
      },
    ],
    bullets: [
      "Simple residential roofs can move quickly once work starts.",
      "Complex detailing, steep slopes, and poor access add time.",
      "Weather and hidden deck repairs are common schedule variables.",
      "An organized crew and proper protection matter more than rushing the timeline.",
    ],
    takeaway:
      "Roof replacement duration depends on complexity and conditions, but controlled execution matters more than promising the shortest possible schedule.",
  },
  {
    slug: "can-you-roof-over-old-shingles",
    question: "Can you put new shingles over old shingles?",
    category: "Installation",
    description:
      "Why overlaying shingles is sometimes proposed, and why full tear-off is often the better long-term decision.",
    heroTitle: "Can You Shingle Over Old Shingles?",
    intro:
      "It is sometimes possible to install new shingles over an existing layer, but whether it can be done and whether it should be done are different questions. In many cases, full tear-off gives a better result because the roof deck and flashing details can actually be inspected.",
    sections: [
      {
        heading: "Overlay saves upfront work but limits visibility",
        paragraphs: [
          "Installing over an existing layer may reduce tear-off labor and disposal, but it also hides the substrate and preserves any unevenness or concealed damage already in the system.",
          "That means less certainty about what the new roof is actually being installed over.",
        ],
      },
      {
        heading: "Full tear-off gives better quality control",
        paragraphs: [
          "A tear-off allows the installer to review decking condition, update flashing details properly, correct problem areas, and start from a cleaner base.",
          "That usually produces a more defensible long-term outcome, especially when the existing roof is aged or uneven.",
        ],
      },
      {
        heading: "Ontario climate makes hidden issues more expensive",
        paragraphs: [
          "If moisture damage, ventilation issues, or freeze-thaw related deterioration are already present, covering them does not solve them.",
          "In Ontario conditions, hidden problems tend to become more expensive when they are deferred rather than addressed.",
        ],
      },
    ],
    bullets: [
      "Overlay can reduce upfront scope but limits inspection of the roof deck.",
      "Full tear-off provides better visibility and better flashing control.",
      "Hidden substrate and moisture issues do not disappear under a new layer.",
      "A cheaper approach upfront can reduce clarity and shorten long-term confidence.",
    ],
    takeaway:
      "An overlay may be possible in some cases, but full tear-off is usually the cleaner and more reliable decision.",
  },
  {
    slug: "how-much-does-roof-repair-cost-ontario",
    question: "How much does roof repair cost in Ontario?",
    category: "Pricing",
    description:
      "What actually drives roof repair pricing in Ontario, from leak tracing and access to material matching and hidden damage.",
    heroTitle: "How Much Does Roof Repair Cost in Ontario?",
    intro:
      "Roof repair pricing is driven less by the phrase 'small repair' and more by how difficult the issue is to diagnose, access, isolate, and correct. A small visible symptom can still involve a complicated repair path.",
    sections: [
      {
        heading: "Leak source matters more than surface appearance",
        paragraphs: [
          "A missing shingle near the leak path is different from a leak tied to wall flashing, valley deterioration, ventilation problems, or moisture traveling through the roof assembly.",
          "Diagnosis quality is part of the repair value, not a separate afterthought.",
        ],
      },
      {
        heading: "Access and matching affect pricing",
        paragraphs: [
          "Steep slopes, winter conditions, limited ladder access, and material-matching challenges can all increase the cost of a repair that sounds minor in conversation.",
          "That is especially true on older roofs where compatible products are harder to source cleanly.",
        ],
      },
      {
        heading: "The goal is not the cheapest patch",
        paragraphs: [
          "A repair only creates value if it addresses the cause of failure and leaves the rest of the roof with realistic remaining service life.",
          "Otherwise, low initial price can become expensive repeat work.",
        ],
      },
    ],
    bullets: [
      "Repair cost depends on diagnosis difficulty as much as material quantity.",
      "Steep access, winter work, and flashing complexity all increase price.",
      "Matching older materials can make repairs more involved.",
      "A cheap patch is not useful if it does not solve the failure path.",
    ],
    takeaway:
      "Ontario roof repair cost depends on the complexity of finding and fixing the real issue, not just on how small the damaged area looks from the ground.",
  },
  {
    slug: "are-roof-inspections-worth-it",
    question: "Are roof inspections worth it?",
    category: "Inspections",
    description:
      "Why a roof inspection often pays for itself by clarifying condition, remaining life, and repair-versus-replacement decisions.",
    heroTitle: "Are Roof Inspections Worth It?",
    intro:
      "A roof inspection is worth it when the cost of uncertainty is higher than the cost of getting clarity. That is usually the case before a purchase, before a large repair, after a storm, or when recurring issues are starting to affect budgeting and planning.",
    sections: [
      {
        heading: "Inspections reduce guesswork",
        paragraphs: [
          "Without a focused review, property owners often end up deciding between patching and replacement based on assumptions, not documented conditions.",
          "An inspection creates a usable baseline instead of relying on sales language or general impressions from the ground.",
        ],
      },
      {
        heading: "They are especially valuable when the next decision is expensive",
        paragraphs: [
          "If the next step could involve a major repair, insurance claim, or full replacement, the value of clear findings is usually obvious.",
          "Inspection cost is small compared with the cost of making the wrong capital decision.",
        ],
      },
      {
        heading: "Not every roof needs one immediately",
        paragraphs: [
          "If the roof is newer, performing well, and not showing warning signs, an inspection may simply be part of periodic maintenance planning rather than an urgent need.",
          "The point is to align the inspection with decision risk.",
        ],
      },
    ],
    bullets: [
      "Inspections are most valuable when repair, replacement, or purchase decisions are pending.",
      "They reduce guesswork and pressure-based recommendations.",
      "The cost is usually minor compared with the cost of a wrong roofing decision.",
      "The value comes from condition clarity, not just from photos.",
    ],
    takeaway:
      "Roof inspections are worth it whenever the cost of uncertainty is high enough to affect money, risk, or timing.",
  },
  {
    slug: "what-causes-ice-dams-on-roofs",
    question: "What causes ice dams on roofs in Ontario?",
    category: "Winter Issues",
    description:
      "How heat loss, ventilation, insulation, and roof edge conditions contribute to ice dam formation in Ontario winters.",
    heroTitle: "What Causes Ice Dams on Roofs?",
    intro:
      "Ice dams are usually a building-performance problem before they are a roofing-material problem. They happen when heat loss and snowmelt interact with cold roof edges, allowing water to refreeze near the eaves.",
    sections: [
      {
        heading: "Heat escaping into the attic is often the starting point",
        paragraphs: [
          "When warm air reaches the underside of the roof deck, snow can melt higher on the roof and then refreeze at colder edges.",
          "That creates the conditions for water backup under shingles and around vulnerable transitions.",
        ],
      },
      {
        heading: "Ventilation and insulation both matter",
        paragraphs: [
          "Poor attic airflow, weak air sealing, and inconsistent insulation all contribute to uneven roof temperatures.",
          "Fixing the symptom at the roof surface without addressing the attic often means the same winter problem returns.",
        ],
      },
      {
        heading: "Roof design can make the issue worse",
        paragraphs: [
          "Complex eave lines, valleys, gutters, and shaded areas can all influence where refreezing happens and how much water gets trapped.",
          "That is why ice dam problems should be assessed as part of the roof-plus-attic system, not just the shingle surface.",
        ],
      },
    ],
    bullets: [
      "Ice dams are often driven by attic heat loss and uneven roof temperatures.",
      "Ventilation, insulation, and air sealing all affect risk.",
      "Roof geometry and eave conditions can worsen water backup.",
      "Treating only the visible ice without fixing the building causes is usually temporary.",
    ],
    takeaway:
      "Ice dams usually come from heat, moisture, and airflow problems interacting with winter roof conditions, not from one simple surface defect.",
  },
  {
    slug: "what-type-of-roof-is-best-for-canadian-winter",
    question: "What type of roof is best for a Canadian winter?",
    category: "Materials",
    description:
      "How roofing material choice, detailing, ventilation, and snow behavior affect winter performance in Canada.",
    heroTitle: "Best Roof Type for a Canadian Winter",
    intro:
      "There is no single roof type that wins in every Canadian winter scenario. Winter performance comes from the full system: material, underlayment, flashing, attic ventilation, insulation, slope design, and installation quality.",
    sections: [
      {
        heading: "Material matters, but system detailing matters more",
        paragraphs: [
          "Shingles, metal, and other roof systems all have strengths, but none of them perform well when flashing, ventilation, and roof-edge details are weak.",
          "The best winter roof is the one installed as a complete system, not the one marketed with the boldest lifespan claim.",
        ],
      },
      {
        heading: "Snow and ice behavior should be considered early",
        paragraphs: [
          "Some roofs retain snow more uniformly, while others shed it faster. Neither behavior is automatically better without considering entrances, walkways, gutters, and site safety.",
          "Winter performance is about control and predictability, not just snow sliding off quickly.",
        ],
      },
      {
        heading: "The house itself affects the answer",
        paragraphs: [
          "Attic design, ventilation paths, insulation quality, and the complexity of the roof all influence which system will perform best in real conditions.",
          "That is why the right answer is property-specific rather than universal.",
        ],
      },
    ],
    bullets: [
      "A complete roofing system matters more than material marketing alone.",
      "Winter performance depends on ventilation, insulation, and flashing quality.",
      "Snow retention and shedding both have tradeoffs that affect property safety.",
      "The right roof type depends on the house, not just on the product category.",
    ],
    takeaway:
      "The best roof for a Canadian winter is the one designed and installed as a complete, climate-appropriate system for the specific property.",
  },
  {
    slug: "does-home-insurance-require-new-roof",
    question: "Will home insurance require me to replace my roof?",
    category: "Insurance",
    description:
      "How roof age, visible condition, underwriting, and renewal risk can affect homeowner insurance requirements.",
    heroTitle: "Will Insurance Require a New Roof?",
    intro:
      "Insurance carriers may not always require replacement immediately, but roof age and visible deterioration can affect underwriting, renewal terms, exclusions, or requests for updated documentation.",
    sections: [
      {
        heading: "Age alone is not the full trigger",
        paragraphs: [
          "Insurers care about risk, and roof age is only one indicator. Visible wear, prior claims, active leaks, and documented condition can all influence how the roof is viewed.",
          "An older roof in solid condition is not the same underwriting risk as an older roof already showing repeated failure.",
        ],
      },
      {
        heading: "Documentation can help clarify the conversation",
        paragraphs: [
          "If coverage or renewal questions are surfacing, a current roof assessment can help establish whether the roof is deteriorated, still serviceable, or moving toward replacement.",
          "That is more useful than letting the conversation revolve around age alone.",
        ],
      },
      {
        heading: "Waiting can reduce flexibility",
        paragraphs: [
          "If the roof is already near the end of service life, delaying decisions may reduce leverage with both insurance and contractors.",
          "Early planning usually creates better options than last-minute compliance replacement.",
        ],
      },
    ],
    bullets: [
      "Insurance decisions are driven by risk, not just the roof's age.",
      "Visible condition and leak history can influence renewal or coverage terms.",
      "Current documentation can help clarify whether replacement is truly necessary.",
      "Earlier planning usually creates better timing and budget control.",
    ],
    takeaway:
      "Insurance may push the issue when roof condition raises risk, so a documented assessment is often the best first step.",
  },
];

export const FAQS_BY_SLUG = Object.fromEntries(FAQS.map((faq) => [faq.slug, faq]));
